// If transparency has slight noise, increase this threshold (e.g., alpha > 5)
const ALPHA_THRESHOLD = 5; 

/**
 * Takes an array of image elements, determines the consolidated,
 * non-transparent bounding box across all frames, and returns a
 * new array of canvas elements containing the cropped frames.
 *
 * @param {HTMLImageElement[]} imageFrames - An array of HTML <img> elements.
 * @returns {HTMLCanvasElement[] | null} An array of cropped canvas elements, or null if input is invalid.
 */
export function cropFramesByConsolidatedTransparency(imageFrames) {
    if (!imageFrames || imageFrames.length === 0) {
        console.error("Input array of imageFrames is empty or invalid.");
        return null;
    }

    // console.log(`Starting consolidated crop for ${imageFrames.length} frames.`);

    // Initialize the bounding box coordinates.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    // --- Step 1: Determine the Consolidated Bounding Box ---
    // Reuse a single canvas for all analysis to save memory and GC pressure
    const analysisCanvas = document.createElement('canvas');
    const analysisCtx = analysisCanvas.getContext('2d', { alpha: true, willReadFrequently: true });

    if (!analysisCtx) {
        console.error(`Could not get 2D context for analysis.`);
        return null;
    }

    imageFrames.forEach((frame, index) => {
        const width = frame.naturalWidth;
        const height = frame.naturalHeight;

        // Resize analysis canvas if frame dimensions differ (usually they are consistent)
        if (analysisCanvas.width !== width || analysisCanvas.height !== height) {
            analysisCanvas.width = width;
            analysisCanvas.height = height;
        }

        // Clear and draw
        analysisCtx.clearRect(0, 0, width, height);
        analysisCtx.drawImage(frame, 0, 0);

        // Get pixel data - this is the most expensive line
        const imageData = analysisCtx.getImageData(0, 0, width, height);
        const data = imageData.data;

        let frameFoundContent = false;

        for (let y = 0; y < height; y++) {
            // Optimization: If this row is outside our current known vertical bounds, 
            // we still check it to expand the bounds.
            for (let x = 0; x < width; x++) {
                // Alpha is the 4th component (index 3, 7, 11...)
                const alpha = data[(y * width + x) * 4 + 3];

                if (alpha > ALPHA_THRESHOLD) {
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                    frameFoundContent = true;
                }
            }
        }
        
        if (frameFoundContent) {
            // console.log(`Frame ${index} processed and contributed to bounds.`);
        } else {
            // console.log(`Frame ${index} is completely transparent.`);
        }
    });

    // Handle case where all frames were entirely transparent
    if (minX === Infinity) {
        console.warn("All frames were completely transparent. No cropping performed.");
        return [];
    }

    const cropWidth = maxX - minX + 1;
    const cropHeight = maxY - minY + 1;
    
    // console.log(`FINAL Consolidated Crop Area: X=${minX}, Y=${minY}, W=${cropWidth}, H=${cropHeight}`);

    // --- Step 2: Crop and Redraw Each Frame ---
    const croppedFrames = [];

    imageFrames.forEach((frame, index) => {
        const newCanvas = document.createElement('canvas');
        newCanvas.width = cropWidth;
        newCanvas.height = cropHeight;
        const newCtx = newCanvas.getContext('2d');

        if (!newCtx) {
            console.error(`Frame ${index}: Could not get 2D context for cropping.`);
            return;
        }

        newCtx.clearRect(0, 0, cropWidth, cropHeight);

        newCtx.drawImage(
            frame,
            minX, // sourceX
            minY, // sourceY
            cropWidth, // sourceW
            cropHeight, // sourceH
            0, // destX
            0, // destY
            cropWidth, // destW
            cropHeight // destH
        );

        croppedFrames.push(newCanvas);
    });

    return croppedFrames;
}